/**
 * Green Flag — CheckInCodeSheet
 *
 * The attendee's check-in code, big enough for a host to scan at the door,
 * with what they need on arrival (how to find the group, parking). Once a
 * host has scanned it, says so instead of showing the code again.
 */

import { Box, Flex, Heading, Image, Text } from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faEnvelope,
  faSun,
} from "@fortawesome/free-solid-svg-icons";
import { MySparkRegistration } from "@greenflagdate/shared";
import { BottomDrawer } from "../BottomDrawer/BottomDrawer";
import { EventArrivalInfo } from "../EventArrivalInfo/EventArrivalInfo";
import { checkInQrSrc } from "../../lib/checkInQrSrc";
import { useStoreData } from "../../lib/store/store";

export interface CheckInCodeSheetProps {
  open: boolean;
  onClose: () => void;
  eventTitle: string;
  /** "Sat, Oct 18 · 7:00 PM", already formatted. */
  when: string;
  location?: string | null;
  registration: MySparkRegistration;
}

export function CheckInCodeSheet({
  open,
  onClose,
  eventTitle,
  when,
  location,
  registration,
}: CheckInCodeSheetProps) {
  const { user } = useStoreData();
  const subtitle = [when, location].filter(Boolean).join(" · ");
  const attendeeName = [user?.first_name, user?.last_name]
    .filter(Boolean)
    .join(" ");
  const checkedIn = !!registration.checked_in_at;

  return (
    <BottomDrawer open={open} onClose={onClose} title="Check-in code">
      <Flex direction="column" align="center" gap="0.875rem" textAlign="center">
        <Box>
          <Heading as="h3" textStyle="displayHeading">
            {eventTitle}
          </Heading>
          <Text textStyle="body" color="fg.muted" mt="0.375rem">
            {subtitle}
          </Text>
        </Box>

        {checkedIn ? (
          <Flex direction="column" align="center" gap="0.75rem" py="1.5rem">
            <Flex
              align="center"
              justify="center"
              boxSize="6rem"
              borderRadius="full"
              bg="bg.brandSoft"
              color="fg.onTint"
              fontSize="2.5rem"
            >
              <FontAwesomeIcon icon={faCheck} />
            </Flex>
            <Text textStyle="displayHeading">
              You're checked in!
            </Text>
            <Text textStyle="body" color="fg.muted">
              Have a great time.
            </Text>
          </Flex>
        ) : (
          <>
            <Box
              p="0.75rem"
              bg="bg.canvas"
              borderWidth="1px"
              borderColor="border.subtle"
              borderRadius="md"
            >
              <Image
                src={checkInQrSrc(registration.check_in_token)}
                alt="Your check-in QR code"
                boxSize="224px"
              />
            </Box>
            <Box>
              {attendeeName && (
                <Text textStyle="sectionHeading">{attendeeName}</Text>
              )}
              <Text textStyle="body" color="fg.muted" mt="0.25rem">
                Show this at the door. Your host scans it to check you in.
              </Text>
            </Box>
          </>
        )}

        {registration.arrival && (
          <Box w="100%" bg="bg.subtle" borderRadius="md" px="1rem" py="0.875rem" textAlign="left">
            <EventArrivalInfo arrival={registration.arrival} />
          </Box>
        )}

        {!checkedIn && (
          <>
            <Flex align="center" gap="0.375rem" color="fg.muted">
              <FontAwesomeIcon icon={faSun} />
              <Text textStyle="finePrint">
                Turn your screen brightness up so the code scans fast.
              </Text>
            </Flex>
            <Flex align="center" gap="0.375rem" color="fg.muted" pb="0.5rem">
              <FontAwesomeIcon icon={faEnvelope} />
              <Text textStyle="finePrint">
                This code is also in your confirmation email.
              </Text>
            </Flex>
          </>
        )}
      </Flex>
    </BottomDrawer>
  );
}
